import React from 'react';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import { useQuery, useMutation } from 'react-fetching-library';
import { Link as RouterLink } from 'react-router-dom';
import { set, sub, formatDistanceToNow } from 'date-fns';
import { Icon } from '@iconify/react';
import bellFill from '@iconify/icons-eva/bell-fill';
import clockFill from '@iconify/icons-eva/clock-fill';
import { alpha } from '@mui/material/styles';
import {
  Box,
  List,
  Badge,
  Button,
  Divider,
  IconButton,
  Typography,
  ListItemText,
  ListSubheader,
  ListItemButton,
  Tooltip
} from '@mui/material';

import Scrollbar from '../../../../components/Scrollbar';
import MenuPopover from '../../../../components/MenuPopover';
import { getAlertsAction, markAlertAsReadAction } from '../../../../services/request';
import { IconButtonAnimate } from '../../../../components/animate';
import Iconify from '../../../../components/Iconify';

function AlertItem({ alert, markAsReadCallback }) {

  return (
    <ListItemButton
      to={'/employee/alert'}
      state={{ alert }}
      params={alert}
      disableGutters
      component={RouterLink}
      sx={{
        py: 1.5,
        px: 2.5,
        mt: '1px',
        ...(alert.isUnRead && {
          bgcolor: 'action.selected'
        })
      }}
    >
      <ListItemText
        primary={
          <Typography variant="subtitle2">
            {alert.title}
            <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }}>
        &nbsp; {alert.description} {_(alert.content).truncate(10)}
            </Typography>
          </Typography>
        }
        secondary={
          <Typography
            variant="caption"
            sx={{
              mt: 0.5,
              display: 'flex',
              alignItems: 'center',
              color: 'text.disabled'
            }}
          >
            <Box component={Icon} icon={clockFill} sx={{ mr: 0.5, width: 16, height: 16 }} />
            {formatDistanceToNow(new Date(alert.created_at))}
            { alert.is_read ? null
              : <Tooltip title=" Oznacz jako przeczytane" onClick={() => markAsReadCallback(alert.id)}>
                <IconButtonAnimate color="primary" >
                  <Iconify icon="eva:done-all-fill" width={20} height={20} />
                </IconButtonAnimate>
              </Tooltip>}
          </Typography>
        }
      />
    </ListItemButton>
  );
}

export default function AlertsPopover() {
  const anchorRef = React.useRef(null);
  const { allowAutoOrderUpdate } = useSelector(state => state.app);
  const { loading, payload, error, query } = useQuery(getAlertsAction());
  const { mutate } = useMutation(markAlertAsReadAction);
  const [open, setOpen] = React.useState(false);
  const [alerts, setAlerts] = React.useState([]);
  const unReadAlerts = alerts.filter(item => item.is_read === false);
  const readAlerts = alerts.filter(item => item.is_read === true);

  React.useEffect(() => {
    if (!error && !loading) {
      setAlerts(payload.results.filter(alert => !alert.content.startsWith('Dla podanych przyjaciół nie znaleziono żadnych wcześniejszych wyjazdów.')));
    }
  }, [loading]);
  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const markAsReadCallback = async id => {
    const { error, payload } = await mutate(id);
    if (!error) {
      const newAlerts = [...alerts];
      const idx = newAlerts.findIndex((obj => obj.id == id));
      newAlerts[idx].is_read = true;
      setAlerts(newAlerts);
    }
  };

  React.useEffect(() => {
    const interval = setInterval(() => {
      query();
    }, 1000 * 60);

    return () => {
      clearInterval(interval);
    };
  }, [query]);

  React.useEffect(() => {
    if (allowAutoOrderUpdate) {
      query();
    }
  }, [allowAutoOrderUpdate]);

  return (
    <>
      <IconButton
        ref={anchorRef}
        size="large"
        color={open ? 'primary' : 'default'}
        onClick={handleOpen}
        sx={{
          ...(open && {
            bgcolor: theme => alpha(theme.palette.primary.main, theme.palette.action.focusOpacity)
          })
        }}
      >
        <Badge badgeContent={unReadAlerts.length} color="error">
          <Icon icon={bellFill} width={20} height={20} />
        </Badge>
      </IconButton>

      <MenuPopover
        open={open}
        onClose={handleClose}
        anchorEl={anchorRef.current}
        sx={{ width: 360 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', py: 2, px: 2.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1">Alerty</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Masz {unReadAlerts.length} nieprzeczytanych wiadomości
            </Typography>
          </Box>
        </Box>

        <Divider />

        <Scrollbar sx={{ height: '70vh' }} >
          <List
            disablePadding
            subheader={
              <ListSubheader disableSticky sx={{ py: 1, px: 2.5, typography: 'overline' }}>
                Nowe
              </ListSubheader>
            }
          >
            {unReadAlerts && unReadAlerts.map(alert => (
              <AlertItem key={alert.id} alert={alert} markAsReadCallback={markAsReadCallback} />
            ))}
          </List>

          <List
            disablePadding
            subheader={
              <ListSubheader disableSticky sx={{ py: 1, px: 2.5, typography: 'overline' }}>
                Przeczytane
              </ListSubheader>
            }
          >
            {readAlerts && readAlerts.map(alert => (
              <AlertItem key={alert.id} alert={alert} />
            ))}
          </List>
        </Scrollbar>

        <Divider />
      </MenuPopover>
    </>
  );
}
